import type { ViewOptionsGroup, ViewOptionsStruct, ViewOption, IObjectViewModel } from '@/engine/app/interfaces';
import { runInAction } from 'mobx';
import type { WithGroupValues, WithUndefined } from '@/components/viewOption/types';

export const isGroup = (item: ViewOptionsGroup | ViewOption): item is ViewOptionsGroup =>
  (item as ViewOptionsGroup | undefined)?.children !== undefined;

interface IObjectAvailabilityChecker {
  isObjectAvailable: (viewOption: ViewOption) => boolean;
}

export const isGroupEmpty = (item: ViewOptionsGroup, checker: IObjectAvailabilityChecker): boolean => {
  if (!item.children.length) {
    return true;
  }
  return item.children.every((viewOption) => !checker.isObjectAvailable(viewOption));
};

const checkGroupProperty = <T>(
  item: ViewOptionsGroup,
  callbackFn: (ViewOption: ViewOption) => WithUndefined<T>
): WithGroupValues<WithUndefined<T>> => {
  if (!item.children.length) {
    return 'empty';
  }
  let result = undefined;
  let prevResult = undefined;
  for (const viewOptionObject of item.children) {
    result = callbackFn(viewOptionObject);

    if (result === undefined) {
      return result;
    }
    if (result !== prevResult && prevResult !== undefined) {
      return 'mixed';
    }
    prevResult = result;
  }

  return result;
};

const resetGroupViewOptions = (id: string, viewOptionsGroups: ViewOptionsStruct): void => {
  const group = (viewOptionsGroups as Array<ViewOptionsGroup>).find((group) => group.id === id);
  runInAction(() => {
    if (group) {
      group.children = [];
    }
  });
};

const addViewOptionToGroup = (
  id: string,
  objectVM: IObjectViewModel<unknown>,
  viewOptionsGroups: ViewOptionsStruct
): void => {
  const group = (viewOptionsGroups as Array<ViewOptionsGroup>).find((group) => group.id === id);

  if (group) {
    group.children.push({ label: objectVM.label, descriptors: [objectVM.descriptor] });
  }
};

const getGroupsCollapsingState = (viewOptions: ViewOptionsStruct | undefined): Record<string, boolean> => {
  return ((viewOptions as Array<ViewOptionsGroup> | undefined) || []).reduce(
    (prevgroup, group) => ({ ...prevgroup, [group.id]: !!group.isCollapsed }),
    {}
  );
};
export interface ViewOptionsStructureManager {
  addViewOptionToGroup: typeof addViewOptionToGroup;
  resetGroupViewOptions: typeof resetGroupViewOptions;
  isGroup: typeof isGroup;
  isGroupEmpty: typeof isGroupEmpty;
  checkGroupProperty: typeof checkGroupProperty;
  getGroupsCollapsingState: typeof getGroupsCollapsingState;
}

export const viewOptionsStructureManager: ViewOptionsStructureManager = {
  addViewOptionToGroup,
  resetGroupViewOptions,
  isGroup,
  isGroupEmpty,
  checkGroupProperty,
  getGroupsCollapsingState
};
